import { useEffect, useState } from "react";
import { apiFetch, planIntoScenario, TENANT } from "../api.js";

export interface Requirement {
  requirementId: string;
  seq: number;
  intentText: string;
  label: string | null;
  terms: { hard: any[]; soft: any[] };
  unmapped: string[];
  /** Time off this requirement wrote to the shared calendar. */
  absences?: { summary: string }[] | null;
  enabled: boolean;
  origin: string;
}

/**
 * What the scenario stands for.
 *
 * A scenario used to be whatever the last run produced, so asking for a second
 * thing meant a second scenario and the first requirement quietly stopped being
 * enforced. Now the list is the definition: every enabled row is satisfied on
 * every solve, and the scenario means "the baseline plus these, together".
 *
 * That makes undo a toggle rather than a snapshot — switch one off, watch the
 * schedule change, switch it back. It also makes conflict legible: an
 * infeasible set names the rows, and the rows are right here to turn off.
 */
export function Requirements({
  scenarioId,
  role,
  onSolved,
  refreshToken = 0,
  resolveScenarioId,
  emptyText,
  bare = false,
}: {
  /** Null when there is no scenario yet — see resolveScenarioId. */
  scenarioId: string | null;
  role: string | undefined;
  /** A re-solve rewrites the schedule, so the board has to reload. */
  onSolved: (note?: string) => void;
  /** Bumped when something else added a requirement — ⌘K, say. */
  refreshToken?: number;
  /**
   * Mint the scenario on first use.
   *
   * Today has no scenario until something is asked of it, and creating one on
   * every visit would copy 166 assignments a day for nothing. So the list can
   * open with no scenario at all and make one when the first requirement is
   * actually added.
   */
  resolveScenarioId?: () => Promise<string | null>;
  /** Replaces the "nothing asked for yet" copy where the context differs. */
  emptyText?: string;
  /** Drop the header strip — the surrounding panel already names it. */
  bare?: boolean;
}) {
  const [reqs, setReqs] = useState<Requirement[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [intent, setIntent] = useState("");
  const [adding, setAdding] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Turning a requirement on or off, or deleting it, is open to managers (on a
  // draft they started, and not one that books time off — the server decides).
  // Adding one runs the planner, which stays an admin's tool.
  const canToggle = role === "admin" || role === "manager";
  const canWrite = role === "admin";

  useEffect(() => { void load(); }, [scenarioId, refreshToken]);

  /**
   * Adding happens here rather than in a panel elsewhere: the list is what a
   * requirement joins, so you should be able to add one without leaving it —
   * and the row appears the moment the solve returns, with no second click to
   * go and look.
   */
  async function add() {
    const text = intent.trim();
    if (!text) return;
    setAdding(true); setErr(null); setElapsed(0);
    const started = Date.now();
    const tick = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    try {
      const target = scenarioId ?? (resolveScenarioId ? await resolveScenarioId() : null);
      if (!target) {
        setErr("Could not open a plan to add that to.");
        return;
      }
      const r = await planIntoScenario(target, text);
      if (!r.ok) { setErr(r.error ?? "Could not add that."); return; }
      setIntent("");
      if (r.requirements) setReqs(r.requirements as Requirement[]);
      else await load();
      if (r.infeasible) setErr(r.note ?? null);
      onSolved(r.infeasible ? undefined : r.note);
    } finally {
      clearInterval(tick);
      setAdding(false);
    }
  }

  async function load() {
    // No scenario yet is a real state, not an error: the list opens empty and
    // the first requirement is what brings one into being.
    if (!scenarioId) { setReqs([]); return; }
    const r = await apiFetch(`/v1/sentry/scenarios/${scenarioId}/requirements?tenantId=${TENANT}`);
    const j = await r.json().catch(() => ({}));
    setReqs(r.ok ? j.requirements ?? [] : []);
  }

  async function act(rid: string, method: "PATCH" | "DELETE", body?: unknown) {
    setBusy(rid); setErr(null);
    try {
      const r = await apiFetch(`/v1/sentry/scenarios/${scenarioId}/requirements/${rid}`, {
        method,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT, ...(body as object) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error ?? `HTTP ${r.status}`); await load(); return; }
      setReqs(j.requirements ?? []);
      if (j.status === "INFEASIBLE") {
        setErr(
          `Those requirements cannot all hold at once${
            j.infeasibleCore?.length ? `: ${j.infeasibleCore.join(" · ")}` : "."
          } The schedule is unchanged.`,
        );
        onSolved();
        return;
      }
      onSolved(`Re-solved — ${j.churn ?? 0} phases changed hands.`);
    } finally { setBusy(null); }
  }

  if (!reqs) return null;

  return (
    <div className={`reqs${bare ? " bare" : ""}`}>
      {!bare && (
        <div className="reqs-head">
          <span className="lab">Requirements</span>
          <span className="chip">{reqs.filter((r) => r.enabled).length} enforced</span>
          <span className="spacer" />
          <span className="muted">every solve satisfies all of these together</span>
        </div>
      )}

      {err && <div className="reqs-err">{err}</div>}

      {!reqs.length ? (
        <div className="muted reqs-empty">
          {emptyText ??
            "Nothing asked for yet. Describe what this scenario should achieve below — it joins this list, and stays enforced as you add more."}
        </div>
      ) : (
        <ol className="reqs-list">
          {reqs.map((r) => (
            <li key={r.requirementId} className={r.enabled ? "" : "off"}>
              <label className="reqs-toggle">
                <input
                  type="checkbox"
                  checked={r.enabled}
                  disabled={!canToggle || busy === r.requirementId}
                  onChange={(e) => void act(r.requirementId, "PATCH", { enabled: e.target.checked })}
                />
                <span className="seq mono">r{r.seq}</span>
              </label>

              <div className="reqs-body">
                <div className="reqs-intent">{r.label ?? r.intentText}</div>
                <div className="reqs-terms">
                  {[...(r.terms?.hard ?? []).map((t: any) => ({ ...t, k: "hard" })),
                    ...(r.terms?.soft ?? []).map((t: any) => ({ ...t, k: "soft" }))].map((t: any, i) => (
                    <span className="term" key={i}>
                      <b className={t.k === "soft" ? "soft" : ""}>{t.k}</b> {t.term}{" "}
                      <span className="sel">{JSON.stringify(t.select)}</span>
                    </span>
                  ))}
                  {!((r.terms?.hard ?? []).length + (r.terms?.soft ?? []).length) &&
                    !r.absences?.length && (
                      <span className="muted">no terms — nothing in this could be expressed</span>
                    )}
                </div>
                {/* Turning this row off lifts the absence too, so what it did to
                    the calendar has to be visible from the row itself. */}
                {!!r.absences?.length && (
                  <div className="reqs-absence">
                    {r.absences.map((a) => <div key={a.summary}>{a.summary}</div>)}
                  </div>
                )}
                {!!r.unmapped?.length && (
                  <div className="reqs-unmapped">
                    could not express: {r.unmapped.map((u) => `“${u}”`).join(", ")}
                  </div>
                )}
              </div>

              <button
                className="btn ghost"
                title="Remove this requirement and re-solve"
                disabled={!canToggle || busy === r.requirementId}
                onClick={() => { if (confirm(`Remove “${r.label ?? r.intentText}” and re-solve?`)) void act(r.requirementId, "DELETE"); }}
              >
                {busy === r.requirementId ? "…" : "✕"}
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="reqs-add">
        <input
          value={intent}
          disabled={!canWrite || adding}
          placeholder={
            canWrite
              ? "add a requirement — “keep Eric Farchmin under 80%”"
              : role === "manager"
                ? "describing a new requirement is an admin's tool — you can switch existing ones on or off"
                : "needs admin access to change this scenario"
          }
          onChange={(e) => setIntent(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
        />
        <button className="btn on" disabled={!canWrite || adding || !intent.trim()} onClick={() => void add()}>
          {adding ? `Solving… ${elapsed}s` : "Add & re-plan"}
        </button>
      </div>
    </div>
  );
}
